import React, {useState, useEffect} from 'react';
import {t, getLanguage} from 'react-switch-lang';
import {
  getAllProcesses,
  getAllSubProcesses,
  getAllDepartments,
} from '../../services/reports/kriReport';
import FormatList from './reportForm/components/format/FormatList';
import DownloadBtn from './reportForm/components/downloadBtn/DownloadBtn';
import {useUserContext} from '../../contexts/UserContext';
import SelectList from '../../components/FormFields/Select';
import * as yup from 'yup';
import {useForm, FormProvider} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers';
import InputDate from '../../components/FormFields/inputDate/InputDate';
import {format} from 'date-fns';
import './Report.scss';
import SelectInfinite from "../../components/FormFields/SelectInfinite";

const ThirdPartiesReport = () => {
  const isValidType = '';
  const lang = getLanguage();
  const [selectedFormat, setSelectedFormat] = useState();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [processes, setProcesses] = useState([]);
  const [processPage, setProcessPage] = useState(0);
  const [hasMoreProcesses, setHasMoreProcesses] = useState(true);
  const [loadingProcesses, setLoadingProcesses] = useState(false);
  const [subProcesses, setSubProcesses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [format2, setFormat2] = useState('');
  const [subprocessPage, setSubprocessPage] = useState(0);
  const [hasMoreSubprocesses, setHasMoreSubprocesses] = useState(true);
  const [loadingSubprocesses, setLoadingSubprocesses] = useState(false);
  const fetchProcesses = async (pageToLoad = 0, replace = false) => {
    try {
      setLoadingProcesses(true);
      const { data } = await getAllProcesses(pageToLoad, 20);

      const mapped = data.map((item) =>
        lang === 'eng'
          ? { value: item.id, label: item.nameEn }
          : { value: item.id, label: item.name }
      );

      setProcesses((prev) => (replace ? mapped : [...prev, ...mapped]));
      setProcessPage(pageToLoad);
      setHasMoreProcesses(data.length === 20);
    } finally {
      setLoadingProcesses(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const {data} = await getAllDepartments();
      setDepartments(data);
      return data;
    } catch (e) {}
  };

  useEffect(() => {
    fetchProcesses(0, true);
    fetchDepartments().then((r) => {
      let departments = [];
      r.forEach((item) => {
        departments.push(
          lang === 'eng'
            ? {value: item.id, label: item.nameEn}
            : {
                value: item.id,
                label: item.name,
              }
        );
      });

      setDepartments(departments);
    });
  }, []);

  const schema = yup.object().shape({
    process: yup.object().nullable().required(t('message.fieldRequired')),
    subprocess: yup.object().nullable().required(t('message.fieldRequired')),
    department: yup.object().nullable().required(t('message.fieldRequired')),
  });

  const riskStatusList = [
    {value: 'OPEN', label: getLanguage() === 'mne' ? 'Otvoren' : 'Open'},
    {value: 'CLOSED', label: getLanguage() === 'mne' ? 'Zatvoren' : 'Closed'},
  ];

  const methods = useForm({
    resolver: yupResolver(schema),
  });

  const {handleSubmit, watch, setValue} = methods;
  const processSelected = watch('process');
  let validFrom = watch('startDate');
  let validTo = watch('endDate');
  let validFromLossDate = watch('lossDateFrom');
  let validToLossDate = watch('lossDateTo');
  let validFromExtendedDate = watch('extendedDeadlineFrom');
  let validToExtendedDate = watch('extendedDeadlineTo');
  const riskStatusValue = watch('riskState');

  const fetchSubprocesses = async (pageToLoad = 0, replace = false) => {
    if (!processSelected?.value) return;

    try {
      setLoadingSubprocesses(true);
      const {data} = await getAllSubProcesses(
        processSelected.value,
        pageToLoad,
        20
      );

      const mapped = data.map((item) =>
        lang === 'eng'
          ? {value: item.id, label: item.nameEn}
          : {value: item.id, label: item.name}
      );

      setSubProcesses(prev =>
        replace ? mapped : [...prev, ...mapped]
      );

      setSubprocessPage(pageToLoad);
      setHasMoreSubprocesses(data.length === 20);
    } catch (e) {
      // opcioni Toast/log
    } finally {
      setLoadingSubprocesses(false);
    }
  };

  useEffect(() => {
    if (processSelected) {
      setSubProcesses([]);
      setSubprocessPage(0);
      setHasMoreSubprocesses(true);
      setValue('subprocess', '', {
        shouldValidate: false,
        shouldDirty: false,
      });
      fetchSubprocesses(0, true);
    } else {
      setSubProcesses([]);
      setSubprocessPage(0);
      setHasMoreSubprocesses(true);
      setValue('subprocess', '', {
        shouldValidate: false,
        shouldDirty: false,
      });
    }

    if (processSelected === null) {
      setValue('subprocess', '', {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [processSelected]);

  const user = useUserContext();

  let userRiskSector = false;
  let responsibleUser = false;
  let userInCharge = false;

  user.authorities.forEach((item) => {
    if (item === 'ROLE_RISK_SECTOR') {
      userRiskSector = true;
    }
    if (item === 'ROLE_RESPONSIBLE_USER') {
      responsibleUser = true;
    }
    if (item === 'ROLE_USER_IN_CHARGE') {
      userInCharge = true;
    }
  });

  useEffect(() => {
    setSelectedFormat();
    setIsSubmitted(false);
  }, []);
  const handleProcessLoadMore = () => {
    if (hasMoreProcesses && !loadingProcesses) {
      fetchProcesses(processPage + 1, false);
    }
  };
  const handleSubprocessLoadMore = () => {
    if (hasMoreSubprocesses && !loadingSubprocesses) {
      fetchSubprocesses(subprocessPage + 1, false);
    }
  };
  return (
    <div className="reports-page content">
      <FormProvider {...methods}>
        <div className="report-form-container">
          <div className="row  ml-0 mr-0">
            <div className="form-group pr-5">
              <label className="label" htmlFor="startDate">
                {t('reports.dateFrom')}
              </label>
              <InputDate
                name="startDate"
                maxDate={validTo}
                defaultValue={
                  new Date(new Date().getFullYear(), new Date().getMonth(), 1)
                }
                className={`date-height form-control ${
                  validTo === null && isSubmitted === true ? `error-field` : ''
                }`}
                isClearable={false}
                placeholderText={t('reports.dateFrom')}
              />
              {validFrom === null && isSubmitted === true ? (
                <span className="error-message position-absolute">
                  {t('message.fieldRequired')}
                </span>
              ) : (
                ''
              )}
            </div>
            <div className="form-group pr-5">
              <label className="label" htmlFor="endDate">
                {t('reports.dateTo')}
              </label>
              <InputDate
                name="endDate"
                minDate={validFrom}
                defaultValue={new Date()}
                className={`date-height form-control ${
                  validTo === null && isSubmitted === true ? `error-field` : ''
                }`}
                isClearable={false}
                placeholderText={t('reports.dateTo')}
              />
              {validTo === null && isSubmitted === true ? (
                <span className="error-message position-absolute">
                  {t('message.fieldRequired')}
                </span>
              ) : (
                ''
              )}
            </div>
            <div className=" form-group pr-5">
              <label className="label w-100" htmlFor="process">
                {t('reports.processes')}
                <span className="optional register line-height">
                  {t('common.optional')}
                </span>
              </label>
              <SelectList
                name="process"
                options={processes}
                placeholder={t('common.select')}
                defaultValue=""
                onLoadMore={handleProcessLoadMore}
                hasMore={hasMoreProcesses}
                isLoading={loadingProcesses}
              />
            </div>

            <div className="form-group pr-5">
              <label className="label w-100" htmlFor="subprocess">
                {t('reports.subprocesses')}
                <span className="optional register line-height">
                  {t('common.optional')}
                </span>
              </label>
              <SelectList
                name="subprocess"
                isDisabled={!processSelected}
                placeholder={t('common.select')}
                options={subProcesses}
                defaultValue=""
                onLoadMore={handleSubprocessLoadMore}
                hasMore={hasMoreSubprocesses}
                isLoading={loadingSubprocesses}
              />
            </div>

            <div className="form-group pr-5">
              <label className="label w-100" htmlFor="department">
                {t('reports.department')}
                <span className="optional register line-height">
                  {t('common.optional')}
                </span>
              </label>
              <SelectList
                name="department"
                options={departments}
                placeholder={t('common.select')}
                defaultValue=""
              />
            </div>
            <div className="form-group pr-5">
              <label className="label w-100" htmlFor="riskType">
                {t('reports.status')}
                <span className="optional register line-height">
                  {t('common.optional')}
                </span>
              </label>
              <SelectList
                name="riskState"
                options={riskStatusList}
                placeholder={t('common.select')}
              />
            </div>

            <div className="form-group form-group-full-width pr-5">
              <label className="label w-100">
                {t('reports.extendedDeadlineFrom')} :
                <span className="optional register line-height">
                  {t('common.optional')}
                </span>
              </label>
              <InputDate
                  name="extendedDeadlineFrom"
                  placeholder={t('codebook.lossDatePlaceholder')}
                  className={'date-input'}
                  maxDate={validToExtendedDate}
              />
            </div>

            <div className="form-group pr-5">
              <label className="label w-100">
                {t('reports.extendedDeadlineTo')} :
                <span className="optional register line-height">
                  {t('common.optional')}
                </span>
              </label>
              <InputDate
                  name="extendedDeadlineTo"
                  placeholder={t('codebook.lossDatePlaceholder')}
                  className={'date-input'}
                  minDate={validFromExtendedDate}
              />
            </div>

            <div className="form-group pr-5">
              <label className="label w-100">
                {t('mitigation-measures.method')}
                <span className="optional register line-height">
                  {t('common.optional')}
                </span>
              </label>
              <SelectInfinite
                  api="/risk-mitigation-modes"
                  name="modesOfMitigation"
                  isMulti={true}
                  labelKey={lang === 'mne' ? 'name' : 'nameEn'}
                  className="select"
                  placeholder={t('placeholder.riskMitigrationMethod')}
              />
            </div>
            <FormatList
              isValidType={isValidType}
              value={['PDF', 'XLSX']}
              parameterName="format"
              onChange={(e) => setFormat2(e)}
              isSubmitted={isSubmitted}
              setIsSubmitted={() => setIsSubmitted(false)}
              setSelectedFormat={(e) => setSelectedFormat(e.toLowerCase())}
              error={!format2.length > 0}
            />
            <DownloadBtn
              api={'reports/risks/third-parties-risk-report'}
              paramsLength={14}
              setIsSubmitted={(e) => setIsSubmitted(e)}
              apiParameters={{
                startDate:
                  validFrom !== null && validFrom !== undefined
                    ? `startDate=${encodeURIComponent(
                        format(validFrom, 'yyyy-MM-dd')
                      )}`
                    : '',
                endDate:
                  validTo !== null && validTo !== undefined
                    ? `endDate=${encodeURIComponent(
                        format(validTo, 'yyyy-MM-dd')
                      )}`
                    : '',
                processId:
                  watch('process') !== null &&
                  watch('process') !== undefined &&
                  watch('process').value !== undefined
                    ? `processId=${watch('process').value}`
                    : `processId=`,
                subprocessId:
                  watch('subprocess') !== null &&
                  watch('subprocess') !== undefined &&
                  watch('subprocess').value !== undefined
                    ? `subprocessId=${watch('subprocess').value}`
                    : `subprocessId=`,
                departmentId:
                  watch('department') !== null &&
                  watch('department') !== undefined &&
                  watch('department').value !== undefined
                    ? `departmentId=${watch('department').value}`
                    : `departmentId=`,
                riskState:
                  riskStatusValue !== null && riskStatusValue !== undefined
                    ? `riskState=${watch('riskState').value}`
                    : 'riskState=',
                format: format2,
                creditRiskRelated:  watch('creditRiskRelated') !== null &&
                watch('creditRiskRelated') !== undefined &&
                watch('creditRiskRelated').label !== undefined
                    ? `creditRiskRelated=${watch('creditRiskRelated').label}`
                    : `creditRiskRelated=`,
                lossReason:  watch('lossReason') !== null &&
                watch('lossReason') !== undefined &&
                watch('lossReason').label !== undefined
                    ? `lossReason=${watch('lossReason').label}`
                    : `lossReason=`,
                modesOfMitigation: Array.isArray(watch('modesOfMitigation')) && watch('modesOfMitigation').length > 0
                    ? `modesOfMitigation=${watch('modesOfMitigation').map(item => item.value).join(',')}`
                    : 'modesOfMitigation=',
                lossDateFrom:  validFromLossDate !== null && validFromLossDate !== undefined
                    ? `lossDateFrom=${encodeURIComponent(
                        format(validFromLossDate, 'yyyy-MM-dd')
                    )}`
                    : 'lossDateFrom=',
                lossDateTo:
                    validToLossDate !== null && validToLossDate !== undefined
                        ? `lossDateTo=${encodeURIComponent(
                            format(validToLossDate, 'yyyy-MM-dd')
                        )}`
                        : 'lossDateTo=',
                extendedDeadlineFrom:  validFromExtendedDate !== null && validFromExtendedDate !== undefined
                    ? `extendedDeadlineFrom=${encodeURIComponent(
                        format(validFromExtendedDate, 'yyyy-MM-dd')
                    )}`
                    : 'extendedDeadlineFrom=',
                extendedDeadlineTo:
                    validToExtendedDate !== null && validToExtendedDate !== undefined
                        ? `extendedDeadlineTo=${encodeURIComponent(
                            format(validToExtendedDate, 'yyyy-MM-dd')
                        )}`
                        : 'extendedDeadlineTo=',
              }}
            />
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

export default ThirdPartiesReport;
